import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { PrimaryButton } from '@/components/PrimaryButton';
import { createCustomer } from '@/features/customers/api';
import { fieldErrorsFrom, validateCustomerForm } from '@/features/customers/customerForm';
import type {
  CustomerDetail,
  CustomerFieldErrors,
  CustomerFormValues,
} from '@/features/customers/types';

const emptyForm: CustomerFormValues = { fullName: '', email: '', phone: '' };

/**
 * Creates a pending account (ADR 0008). A mail that already has one hands that account back
 * instead, and `onDone` says which of the two happened so the sheet can tell the employee.
 */
export function NewCustomerDialog({
  token,
  open,
  onClose,
  onDone,
}: {
  token: string;
  open: boolean;
  onClose: () => void;
  onDone: (customer: CustomerDetail, created: boolean) => void;
}) {
  const [values, setValues] = useState<CustomerFormValues>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<CustomerFieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const close = () => {
    if (isSaving) {
      return;
    }

    setValues(emptyForm);
    setFieldErrors({});
    setError(null);
    onClose();
  };

  const setField = (field: keyof CustomerFormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    // The mark goes away as soon as the field is touched again.
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const problems = validateCustomerForm(values, { phoneRequired: false });

    if (Object.keys(problems).length) {
      setFieldErrors(problems);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const result = await createCustomer(token, {
        fullName: values.fullName.trim(),
        email: values.email.trim(),
        ...(values.phone.trim() ? { phone: values.phone.trim() } : {}),
      });

      setValues(emptyForm);
      onDone(result.customer, result.created);
    } catch (cause) {
      const marked = fieldErrorsFrom(cause);

      // Includes the mail of a staff account or of a deactivated one, which the backend marks
      // on the mail field in Spanish.
      if (marked) {
        setFieldErrors(marked);
      } else {
        setError('No pudimos crear la cuenta. Revisá la conexión e intentá de nuevo.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <form noValidate onSubmit={handleSubmit}>
        <DialogTitle>Nueva cuenta de cliente</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Alert severity="info">
              La cuenta queda pendiente y le mandamos al mail una invitación para que elija su
              contraseña. Si el mail ya tiene cuenta, te llevamos a esa.
            </Alert>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              autoFocus
              required
              fullWidth
              label="Nombre"
              value={values.fullName}
              error={Boolean(fieldErrors.fullName)}
              helperText={fieldErrors.fullName}
              onChange={(event) => setField('fullName', event.target.value)}
            />
            <TextField
              required
              fullWidth
              type="email"
              label="Mail"
              value={values.email}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email || 'Ahí le llega la invitación.'}
              onChange={(event) => setField('email', event.target.value)}
            />
            <TextField
              fullWidth
              type="tel"
              label="Teléfono (opcional)"
              value={values.phone}
              error={Boolean(fieldErrors.phone)}
              helperText={
                fieldErrors.phone || 'Si no lo cargás, se lo pedimos cuando active la cuenta.'
              }
              onChange={(event) => setField('phone', event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={close} disabled={isSaving}>
            Cancelar
          </Button>
          <PrimaryButton type="submit" disabled={isSaving}>
            {isSaving ? 'Creando...' : 'Crear cuenta'}
          </PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
